/**
 * Character Store (Zustand)
 *
 * Replaces CharacterDataProvider with a simpler Zustand store.
 * Manages multi-character state with persistence to localStorage.
 *
 * Features:
 * - Multi-character support
 * - Auto-persistence via Zustand middleware
 * - Granular subscriptions (no unnecessary re-renders)
 * - Direct store access (no provider wrapper needed)
 * - Migration from old CharacterCollection format
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CharacterData, Relationship, Table } from '~/types/storage';
import { UNCATEGORIZED_TABLE_ID } from '~/types/storage';
import { createDefaultCharacterData } from '~/utils/characterDataFactory';
import { tryParseCharacterCollection } from '~/types/storage';
import {
  exportCharacterData as exportCharacterDataUtil,
  importCharacterData as importCharacterDataUtil,
} from '~/utils/characterStorage';
import { generateUUID } from '~/utils/uuid';

interface CharacterStore {
  // State
  characters: CharacterData[];
  tables: Table[];
  activeTableId: string;

  // Core character actions
  setActiveCharacter: (id: string) => void;
  addCharacter: (character: CharacterData) => void;
  removeCharacter: (id: string) => void;
  updateCharacter: (id: string, updates: Partial<CharacterData>) => void;

  // Table actions
  addTable: (name: string) => void;
  removeTable: (id: string) => void;
  renameTable: (id: string, name: string) => void;
  setActiveTable: (id: string) => void;
  assignCharacterToTable: (characterId: string, tableId: string) => void;

  // Helpers
  getActiveCharacter: () => CharacterData;
  getCharacterById: (id: string) => CharacterData | undefined;
  getActiveTable: () => Table | undefined;
  getCharactersForActiveTable: () => CharacterData[];

  // Individual field updates for active character
  updateName: (name: string) => void;
  updateCoreStats: (coreStats: Record<string, number>) => void;
  updateCurrentHp: (currentHp: number) => void;
  updateCurrentResilience: (currentResilience: number) => void;
  updateStrain: (strain: number) => void;
  updateItems: (items: { id: string; content: string }[]) => void;
  updateAppearance: (appearance: string) => void;
  updateRelationships: (relationships: Relationship[]) => void;
  updateNotes: (notes: string) => void;

  // Bulk replace active character (for import)
  setCharacterData: (data: CharacterData) => void;

  // Import/Export
  exportCharacter: () => void;
  importCharacter: (file: File) => Promise<void>;
  importCharacterToCollection: (file: File) => Promise<void>;
}

export const useCharacterStore = create<CharacterStore>()(
  persist(
    (set, get) => {
      // Helper to update active character
      const updateActiveCharacter = (updates: Partial<CharacterData>) => {
        const activeChar = get().getActiveCharacter();
        if (!activeChar) return;

        const { characters } = get();
        set({
          characters: characters.map((c) =>
            c.id === activeChar.id ? { ...c, ...updates } : c
          ),
        });
      };

      return {
        // Initial state - will be overwritten by persisted data
        characters: [createDefaultCharacterData()],
        tables: [{
          id: UNCATEGORIZED_TABLE_ID,
          name: 'Uncategorized',
          activeCharacterId: '',
        }],
        activeTableId: UNCATEGORIZED_TABLE_ID,

        // Core character actions
        setActiveCharacter: (id) => {
          const { characters, tables, activeTableId } = get();
          if (characters.find((c) => c.id === id)) {
            // Update active character for current table
            set({
              tables: tables.map((t) =>
                t.id === activeTableId ? { ...t, activeCharacterId: id } : t
              ),
            });
          }
        },

        addCharacter: (character) =>
          set((state) => {
            // Assign new character to current table
            const charWithTable = {
              ...character,
              belongsToTable: character.belongsToTable || state.activeTableId,
            };
            return {
              characters: [...state.characters, charWithTable],
            };
          }),

        removeCharacter: (id) =>
          set((state) => {
            const remaining = state.characters.filter((c) => c.id !== id);
            if (remaining.length === 0) {
              const newChar = createDefaultCharacterData();
              newChar.belongsToTable = state.activeTableId;
              return {
                characters: [newChar],
                tables: state.tables.map((t) =>
                  t.id === state.activeTableId
                    ? { ...t, activeCharacterId: newChar.id }
                    : t
                ),
              };
            }

            // Update table's active character if we deleted it
            const activeTable = state.tables.find((t) => t.id === state.activeTableId);
            const needsNewActive = activeTable?.activeCharacterId === id;
            const tableCharacters = remaining.filter(
              (c) => c.belongsToTable === state.activeTableId
            );
            const newActiveId = needsNewActive && tableCharacters.length > 0
              ? tableCharacters[0].id
              : activeTable?.activeCharacterId;

            return {
              characters: remaining,
              tables: state.tables.map((t) =>
                t.id === state.activeTableId
                  ? { ...t, activeCharacterId: newActiveId }
                  : t
              ),
            };
          }),

        updateCharacter: (id, updates) =>
          set((state) => ({
            characters: state.characters.map((c) =>
              c.id === id ? { ...c, ...updates } : c
            ),
          })),

        // Table actions
        addTable: (name) =>
          set((state) => {
            const newTable: Table = {
              id: generateUUID(),
              name,
              activeCharacterId: undefined,
            };
            return {
              tables: [...state.tables, newTable],
            };
          }),

        removeTable: (id) =>
          set((state) => {
            if (id === UNCATEGORIZED_TABLE_ID) {
              // Cannot delete uncategorized table
              return state;
            }

            // Move characters from deleted table to uncategorized
            const characters = state.characters.map((c) =>
              c.belongsToTable === id
                ? { ...c, belongsToTable: UNCATEGORIZED_TABLE_ID }
                : c
            );

            const tables = state.tables.filter((t) => t.id !== id);
            const newActiveTableId =
              state.activeTableId === id ? UNCATEGORIZED_TABLE_ID : state.activeTableId;

            return {
              characters,
              tables,
              activeTableId: newActiveTableId,
            };
          }),

        renameTable: (id, name) =>
          set((state) => ({
            tables: state.tables.map((t) => (t.id === id ? { ...t, name } : t)),
          })),

        setActiveTable: (id) => {
          const { tables, characters } = get();
          const targetTable = tables.find((t) => t.id === id);
          if (!targetTable) return;

          // Verify the table's activeCharacterId is valid
          const activeCharId = targetTable.activeCharacterId;
          const activeChar = activeCharId
            ? characters.find((c) => c.id === activeCharId)
            : undefined;

          // If activeCharacterId is invalid or doesn't belong to this table, fix it
          if (!activeChar || activeChar.belongsToTable !== id) {
            const tableCharacters = characters.filter((c) => c.belongsToTable === id);
            const newActiveCharId = tableCharacters[0]?.id;

            set({
              activeTableId: id,
              tables: tables.map((t) =>
                t.id === id ? { ...t, activeCharacterId: newActiveCharId } : t
              ),
            });
          } else {
            // ActiveCharacterId is valid, just switch tables
            set({ activeTableId: id });
          }
        },

        assignCharacterToTable: (characterId, tableId) =>
          set((state) => {
            // Find the character being moved
            const character = state.characters.find((c) => c.id === characterId);
            if (!character) return state;

            const sourceTableId = character.belongsToTable;

            // Update character's table assignment
            const characters = state.characters.map((c) =>
              c.id === characterId ? { ...c, belongsToTable: tableId } : c
            );

            // Update table activeCharacterIds
            const tables = state.tables.map((table) => {
              // If this character was the active character in the source table, clear it
              if (table.id === sourceTableId && table.activeCharacterId === characterId) {
                // Find another character still in this table
                const remainingChars = characters.filter(
                  (c) => c.belongsToTable === sourceTableId && c.id !== characterId
                );
                return {
                  ...table,
                  activeCharacterId: remainingChars[0]?.id,
                };
              }
              // Optionally set as active in destination table if it has no active character
              if (table.id === tableId && !table.activeCharacterId) {
                return {
                  ...table,
                  activeCharacterId: characterId,
                };
              }
              return table;
            });

            return { characters, tables };
          }),

        // Helpers
        getActiveCharacter: () => {
          const { characters, tables, activeTableId } = get();
          const activeTable = tables.find((t) => t.id === activeTableId);
          const activeCharacterId = activeTable?.activeCharacterId;

          if (activeCharacterId) {
            const char = characters.find((c) => c.id === activeCharacterId);
            if (char) return char;
          }

          // Fallback: first character in active table
          const tableCharacters = characters.filter(
            (c) => c.belongsToTable === activeTableId
          );
          return tableCharacters[0] ?? characters[0];
        },

        getCharacterById: (id) => get().characters.find((c) => c.id === id),

        getActiveTable: () => {
          const { tables, activeTableId } = get();
          return tables.find((t) => t.id === activeTableId);
        },

        getCharactersForActiveTable: () => {
          const { characters, activeTableId } = get();
          return characters.filter((c) => c.belongsToTable === activeTableId);
        },

        // Individual field updates for active character
        updateName: (name) => updateActiveCharacter({ name }),
        updateCoreStats: (coreStats) => updateActiveCharacter({ coreStats }),
        updateCurrentHp: (currentHp) => updateActiveCharacter({ currentHp }),
        updateCurrentResilience: (currentResilience) =>
          updateActiveCharacter({ currentResilience }),
        updateStrain: (strain) => updateActiveCharacter({ strain }),
        updateItems: (items) => updateActiveCharacter({ items }),
        updateAppearance: (appearance) => updateActiveCharacter({ appearance }),
        updateRelationships: (relationships) =>
          updateActiveCharacter({ relationships }),
        updateNotes: (notes) => updateActiveCharacter({ notes }),

        // Bulk replace active character (for import)
        setCharacterData: (data) => {
          const activeChar = get().getActiveCharacter();
          if (!activeChar) return;
          // Keep the same ID but replace all other data
          updateActiveCharacter({ ...data, id: activeChar.id });
        },

        // Import/Export
        exportCharacter: () => {
          const character = get().getActiveCharacter();
          const filename = character.name.replace(/\s+/g, '_') || 'character';
          exportCharacterDataUtil(character, filename);
        },

        importCharacter: async (file) => {
          const data = await importCharacterDataUtil(file);
          get().setCharacterData(data);
        },

        importCharacterToCollection: async (file) => {
          const data = await importCharacterDataUtil(file);
          get().addCharacter(data);
        },
      };
    },
    {
      name: 'divide_ttrpg_characters', // Same key for seamless migration
      version: 2,
      migrate: (persistedState: unknown, version: number) => {
        /**
         * Migration Strategy:
         *
         * Version 0 (or missing): Old CharacterCollection format (pre-Zustand)
         *   - Format: { activeCharacterId: "...", characters: [...] }
         *   - Use tryParseCharacterCollection to handle:
         *     a) Collection structure validation
         *     b) Individual character migrations (CHARACTER_DATA_VERSION v1→v4)
         *
         * Version 1: Zustand store without tables
         *   - Format: { characters: [...], activeCharacterId: "..." }
         *   - Migrate to tables structure
         *
         * Version 2: Current Zustand store with tables
         *   - Format: { characters: [...], tables: [...], activeTableId: "..." }
         *   - No migration needed
         *
         * Note: Collection-level versioning removed - never had any migrations.
         * Future collection-level changes should increment Zustand version and add migration here.
         */
        if (version === 0 || !version) {
          // Migrate from old CharacterCollection format
          const collection = tryParseCharacterCollection(persistedState);
          if (collection) {
            // Create uncategorized table with existing activeCharacterId
            const uncategorizedTable: Table = {
              id: UNCATEGORIZED_TABLE_ID,
              name: 'Uncategorized',
              activeCharacterId: collection.activeCharacterId,
            };

            // Assign all characters to uncategorized
            const characters = collection.characters.map((c) => ({
              ...c,
              belongsToTable: c.belongsToTable || UNCATEGORIZED_TABLE_ID,
            }));

            return {
              characters,
              tables: [uncategorizedTable],
              activeTableId: UNCATEGORIZED_TABLE_ID,
            };
          }

          // Migration failed - will fall through to default initialization
          // eslint-disable-next-line no-console
          console.warn('Failed to migrate old character data, using defaults');
        }

        if (version < 2) {
          // Migrate from v1 (no tables) to v2 (with tables)
          const state = persistedState as {
            characters: CharacterData[];
            activeCharacterId: string;
          };

          // Create uncategorized table with existing activeCharacterId
          const uncategorizedTable: Table = {
            id: UNCATEGORIZED_TABLE_ID,
            name: 'Uncategorized',
            activeCharacterId: state.activeCharacterId,
          };

          // Assign all characters to uncategorized
          const characters = state.characters.map((c) => ({
            ...c,
            belongsToTable: c.belongsToTable || UNCATEGORIZED_TABLE_ID,
          }));

          return {
            characters,
            tables: [uncategorizedTable],
            activeTableId: UNCATEGORIZED_TABLE_ID,
          };
        }

        // Version 2 is current format, return as-is
        return persistedState as Partial<CharacterStore>;
      },
      // Only persist characters, tables, and activeTableId (methods/computed values excluded)
      partialize: (state) => ({
        characters: state.characters,
        tables: state.tables,
        activeTableId: state.activeTableId,
      }),
      // Normalize character table assignments after rehydration
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Ensure all characters belong to valid tables
          const validTableIds = new Set(state.tables.map((t) => t.id));
          const normalized = state.characters.map((c) => {
            if (!c.belongsToTable || !validTableIds.has(c.belongsToTable)) {
              return { ...c, belongsToTable: UNCATEGORIZED_TABLE_ID };
            }
            return c;
          });

          // Update state if changes were made
          const hasChanges = normalized.some(
            (c, i) => c.belongsToTable !== state.characters[i].belongsToTable
          );
          if (hasChanges) {
            state.characters = normalized;
          }
        }
      },
    }
  )
);
